import React, { useEffect, useMemo, useRef, useState } from "react";
import { Copy, Check, ArrowLeft, Pencil, Zap } from "lucide-react";
import { formatBotMessage, formatTimestamp } from "./utils/formatMessage";

/**
 * StreamingMessage — a single bot message bubble. Re-formats `text` on every
 * render so it can grow incrementally as tse:chunk events append to it; the
 * blinking cursor (CSS ::after on .d5-cursor) only shows while isStreaming
 * and not yet isDone.
 */
const capName = (name) => {
  if (!name) return "";
  const s = String(name).trim();
  if (s.length > 10) {
    return s.slice(0, 10) + "…";
  }
  return s;
};

const StreamingMessage = ({ text, sources = [], isStreaming = false, isDone = true, cacheHit = false, timestamp, sender = "copilot", botName = "Copilot", onCopy, onShare, onEdit }) => {
  const [showActions, setShowActions] = useState(false);
  // Sources are part of the render: they turn [S1] markers into numbered pills.
  const html = useMemo(() => formatBotMessage(text, sources), [text, sources]);
  const showCursor = isStreaming && !isDone;

  // Copy said nothing at all — the clipboard write is invisible, so the only
  // way to know it worked was to paste somewhere and look. Confirmed in place
  // with the tick, the same idiom the query-copy button already uses
  // (CopilotPanel's copiedQueryId), rather than a toast this panel has no
  // system for.
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef(null);
  // The timer must not fire into an unmounted bubble — a message can be
  // replaced mid-stream, and setState after that is a leak warning.
  useEffect(() => () => {
    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
  }, []);

  const handleCopy = async () => {
    try {
      // AWAITED. writeText returns a promise and rejects on an insecure origin
      // or a denied permission; fired and forgotten, a failed copy still showed
      // the tick and told the reader their text was on the clipboard when it
      // was not.
      await navigator.clipboard?.writeText(text || "");
      setCopied(true);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopied(false), 1600);
      onCopy?.(text);
    } catch {
      // Left silent and unconfirmed: no tick is the honest signal, and the text
      // is still selectable by hand.
    }
  };

  return (
    <div
      className={`d5-msg d5-msg-${sender}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className={`d5-msg-bubble ${showCursor ? "d5-msg-streaming" : ""}`}>
        <span dangerouslySetInnerHTML={{ __html: html }} />
        {showCursor && <span className="d5-cursor" aria-hidden="true" />}
      </div>

      <div className="d5-msg-meta">
        {timestamp && <span className={`d5-msg-timestamp ${showActions ? "d5-msg-timestamp-visible" : ""}`}>{`${capName(botName || "Copilot")} ${formatTimestamp(timestamp)}`}</span>}
        {cacheHit && (
          <span className="d5-cache-indicator" title="Response served from cache">
            <Zap size={11} strokeWidth={2} />
          </span>
        )}
        {isDone && text && (
          <div className="d5-msg-actions">
            <button
              type="button"
              className={`d5-icon-btn${copied ? " d5-icon-btn-ok" : ""}`}
              title={copied ? "Copied to clipboard" : "Copy"}
              aria-label={copied ? "Copied to clipboard" : "Copy"}
              onClick={handleCopy}
            >
              {copied ? <Check size={13} /> : <Copy size={13} />}
            </button>
            {onEdit && (
              <button type="button" className="d5-icon-btn" title="Edit" onClick={() => onEdit(text)}>
                <Pencil size={13} />
              </button>
            )}
            {onShare && (
              /* A left arrow, not a share glyph: this does not share anywhere,
                 it pushes the answer into the customer chat — which is the
                 pane immediately to the left. */
              <button type="button" className="d5-icon-btn" title="Send to customer" onClick={() => onShare(text)}>
                <ArrowLeft size={13} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StreamingMessage;
